import { createContext, useCallback, useContext, useRef, useState } from "react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import FilePreviewModal from "@/components/common/FilePreviewModal";
import PlanViewer from "@/components/common/PlanViewer";

/**
 * FilePreviewContext — spec 2026-07-20.
 *
 * Corrige le blocage popup navigateur : les anciens boutons "Voir" appelaient
 * `window.open(blobUrl)` *après* un `await axios.get(...)` — Chrome/Edge/
 * Firefox bloquent alors la fenêtre (l'ouverture n'est plus considérée comme
 * une action utilisateur directe).
 *
 * La solution : afficher le fichier dans une modale intégrée (aucune fenêtre
 * externe ouverte). Fournit également un bouton "Télécharger" à côté du
 * bouton "Voir" via un blob URL avec attribut `download`.
 *
 * Usage :
 *
 *   const { openAuthenticatedUrl, buildDownloadHandler } = useFilePreview();
 *
 *   <button onClick={() => openAuthenticatedUrl(url, { filename })}>Voir</button>
 *   <button onClick={buildDownloadHandler(url, { filename })}>Télécharger</button>
 */
const FilePreviewContext = createContext(null);

/** YouTube / Vimeo → URL embarquable ; sinon l'URL telle quelle. */
const embedUrl = (u) => {
  const yt = /(?:youtube\.com\/(?:watch\?v=|shorts\/|embed\/)|youtu\.be\/)([\w-]{6,})/i.exec(u);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = /vimeo\.com\/(\d+)/i.exec(u);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return u;
};

// Extract filename from Content-Disposition header (RFC 6266 / legacy).
const _filenameFromHeaders = (headers) => {
  const cd = headers?.["content-disposition"] || headers?.["Content-Disposition"];
  if (!cd) return null;
  // Prefer filename*=UTF-8''... over filename="...".
  const star = /filename\*\s*=\s*(?:UTF-8'')?([^;]+)/i.exec(cd);
  if (star && star[1]) {
    try { return decodeURIComponent(star[1].trim().replace(/^"|"$/g, "")); } catch { /* noop */ }
  }
  const plain = /filename\s*=\s*"?([^";]+)"?/i.exec(cd);
  return plain ? plain[1].trim() : null;
};

export function FilePreviewProvider({ children }) {
  // { url (blob:), filename, contentType } | null
  const [preview, setPreview] = useState(null);
  // Track blob URLs we allocated so we can revoke them on close.
  const blobRef = useRef(null);

  const closePreview = useCallback(() => {
    if (blobRef.current) {
      try { URL.revokeObjectURL(blobRef.current); } catch { /* noop */ }
      blobRef.current = null;
    }
    setPreview(null);
  }, []);

  /**
   * openAuthenticatedUrl(rawUrl, { filename, contentType }?)
   *   - Télécharge le fichier via axios (token joint automatiquement).
   *   - Crée un blob URL local et l'affiche dans la modale intégrée.
   *   - Aucun `window.open` — donc jamais bloqué par le navigateur.
   */
  const openAuthenticatedUrl = useCallback(async (rawUrl, opts = {}) => {
    if (!rawUrl) return;
    // iter380 pt 1 — lien externe (vidéo en ligne) : lecteur intégré dans la visionneuse, jamais un nouvel onglet.
    if (/^https?:\/\//i.test(String(rawUrl)) && !String(rawUrl).includes("/api/")) {
      if (blobRef.current) { try { URL.revokeObjectURL(blobRef.current); } catch { /* noop */ } blobRef.current = null; }
      setPreview({ external: embedUrl(rawUrl), filename: opts.filename || rawUrl, contentType: "", gallery: opts.gallery || null, index: opts.index ?? 0 });
      return;
    }
    // Outil « Plan » explicite (pages à la demande, impression) — sinon la visionneuse partagée affiche le PDF avec défilement des pages.
    const crm = /\/crm\/files\/([^/]+)\/download/.exec(String(rawUrl));
    if (crm && opts.plan) {
      setPreview({ plan: { fileId: crm[1], filename: opts.filename || "plan.pdf", contentType: opts.contentType || "application/pdf" } });
      return;
    }
    try {
      const path = String(rawUrl).replace(/^\/api/, "");
      const resp = await api.get(path, { responseType: "blob", onDownloadProgress: opts.onProgress ? (e) => opts.onProgress(e.total ? Math.round(e.loaded / e.total * 100) : null, e.loaded, e.total) : undefined });
      // Revoke previously held blob URL, if any.
      if (blobRef.current) {
        try { URL.revokeObjectURL(blobRef.current); } catch { /* noop */ }
      }
      const blobUrl = URL.createObjectURL(resp.data);
      blobRef.current = blobUrl;
      const ct = opts.contentType
        || resp.headers?.["content-type"]
        || resp.data?.type
        || "";
      const fname = opts.filename
        || _filenameFromHeaders(resp.headers)
        || "fichier";
      // iter172 — galerie optionnelle : { gallery: [{url, filename, contentType}], index }
      setPreview({ url: blobUrl, filename: fname, contentType: ct, pdfFileId: crm && (ct === "application/pdf" || /\.pdf$/i.test(fname)) ? crm[1] : null, gallery: opts.gallery || null, index: opts.index ?? 0 });
    } catch (err) {
      toast.error("Ouverture impossible", { description: err?.response?.data?.detail || err?.message || "" });
    }
  }, []);

  /**
   * buildDownloadHandler(rawUrl, { filename }?) — retourne un onClick handler
   * qui télécharge le fichier via axios et déclenche `<a download>`. Comme
   * l'ancrage est créé et cliqué de façon synchrone après un unique await
   * (aucun `window.open`), les navigateurs n'ouvrent pas de popup à bloquer.
   */
  const buildDownloadHandler = useCallback((rawUrl, opts = {}) => async (e) => {
    e?.preventDefault?.();
    if (!rawUrl) return;
    try {
      const path = String(rawUrl).replace(/^\/api/, "");
      const resp = await api.get(path, { responseType: "blob" });
      const blobUrl = URL.createObjectURL(resp.data);
      const fname = opts.filename
        || _filenameFromHeaders(resp.headers)
        || "fichier";
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = fname;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => { try { URL.revokeObjectURL(blobUrl); } catch { /* noop */ } }, 5000);
    } catch (err) {
      toast.error("Téléchargement impossible", { description: err?.response?.data?.detail || err?.message || "" });
    }
  }, []);

  const navigate = useCallback((dir) => {
    const g = preview?.gallery;
    if (!g || g.length < 2) return;
    const next = (preview.index + dir + g.length) % g.length;
    const it = g[next];
    openAuthenticatedUrl(it.url, { filename: it.filename, contentType: it.contentType, gallery: g, index: next });
  }, [preview, openAuthenticatedUrl]);

  const value = { openAuthenticatedUrl, buildDownloadHandler, setPreview, closePreview };

  return (
    <FilePreviewContext.Provider value={value}>
      {children}
      {preview?.plan && <PlanViewer {...preview.plan} onClose={closePreview} />}
      <FilePreviewModal
        open={!!preview && !preview.plan}
        onOpenChange={(v) => { if (!v) closePreview(); }}
        fileUrl={preview?.url}
        externalUrl={preview?.external}
        pdfFileId={preview?.pdfFileId}
        filename={preview?.filename}
        contentType={preview?.contentType}
        galleryCount={preview?.gallery?.length || 0}
        galleryIndex={preview?.index || 0}
        onNavigate={navigate}
      />
    </FilePreviewContext.Provider>
  );
}

export function useFilePreview() {
  const ctx = useContext(FilePreviewContext);
  if (!ctx) throw new Error("useFilePreview must be used within a FilePreviewProvider");
  return ctx;
}
